import { useState, useEffect, createContext, useContext } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Icon from '../AppIcon';
import AppImage from '../AppImage';
import { useAuth } from '../../contexts/AuthContext';
import { authService } from '../../services/authService';

const SidebarContext = createContext({
  isCollapsed: false,
  toggleCollapse: () => {},
});

export const useSidebar = () => {
  const context = useContext(SidebarContext);
  if (!context) {
    throw new Error('useSidebar must be used within SidebarProvider');
  }
  return context;
};

export const SidebarProvider = ({ children }) => {
  const [isCollapsed, setIsCollapsed] = useState(() => {
    const saved = localStorage.getItem('sidebar-collapsed');
    return saved ? JSON.parse(saved) : false;
  });

  const toggleCollapse = () => {
    setIsCollapsed(prev => {
      const newValue = !prev;
      localStorage.setItem('sidebar-collapsed', JSON.stringify(newValue));
      return newValue;
    });
  };

  return (
    <SidebarContext.Provider value={{ isCollapsed, toggleCollapse }}>
      {children}
    </SidebarContext.Provider>
  );
};

export default function Sidebar({ isOpen, onClose }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, profile } = useAuth();
  const { isCollapsed, toggleCollapse } = useSidebar();

  const [systemStatus] = useState({
    erp: 'connected',
    banking: 'connected',
    shipping: 'connected',
  });

  const navigationItems = [
    {
      group: 'Dashboard',
      items: [
        {
          label: 'Operations Dashboard',
          path: '/operations-dashboard',
          icon: 'LayoutDashboard',
        },
      ],
    },
    {
      group: 'Trading Operations',
      items: [
        {
          label: 'Shipment Register',
          path: '/shipment-register',
          icon: 'Ship',
        },
        {
          label: 'Cost Register',
          path: '/cost-register',
          icon: 'DollarSign',
        },
        {
          label: 'Fee Register',
          path: '/fee-register',
          icon: 'Receipt',
        },
      ],
    },
    {
      group: 'Financial Management',
      items: [
        {
          label: 'General Ledger',
          path: '/general-ledger',
          icon: 'BookOpen',
        },
        {
          label: 'Accounts Payable',
          path: '/accounts-payable',
          icon: 'CreditCard',
        },
        {
          label: 'Accounts Receivable',
          path: '/accounts-receivable',
          icon: 'Wallet',
        },
        {
          label: 'Invoices',
          path: '/invoices',
          icon: 'FileText',
        },
        {
          label: 'Bank Reconciliation',
          path: '/bank-reconciliation',
          icon: 'Landmark',
        },
        {
          label: 'Exchange Rates',
          path: '/exchange-rates',
          icon: 'ArrowLeftRight',
        },
        {
          label: 'Financial Statements',
          path: '/financial-statements',
          icon: 'TrendingUp',
        },
      ],
    },
    {
      group: 'Operations Management',
      items: [
        {
          label: 'Issue & Risk Log',
          path: '/issue-and-risk-log',
          icon: 'AlertTriangle',
        },
        {
          label: 'Monthly Review Notes',
          path: '/monthly-review-notes',
          icon: 'FileText',
        },
      ],
    },
    {
      group: 'System Administration',
      items: [
        {
          label: 'User & Role Management',
          path: '/user-and-role-management',
          icon: 'Users',
        },
        {
          label: 'Master Data Management',
          path: '/master-data-management',
          icon: 'Database',
        },
        {
          label: 'Document Management',
          path: '/document-management-center',
          icon: 'FolderOpen',
        },
      ],
    },
    {
      group: 'Reports & Analytics',
      items: [
        {
          label: 'Reports & Analytics',
          path: '/reports-and-analytics',
          icon: 'BarChart3',
        },
      ],
    },
    {
      group: 'Account',
      items: [
        {
          label: 'Logout',
          path: '/logout',
          icon: 'LogOut',
          action: 'logout'
        },
      ],
    },
  ];

  const handleNavigation = (path, action) => {
    if (action === 'logout') {
      handleSignOut();
      return;
    }
    navigate(path);
    if (window.innerWidth < 1024) {
      onClose();
    }
  };

  const isActive = (path) => location?.pathname === path;

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024 && isOpen) {
        onClose();
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isOpen, onClose]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'connected':
        return 'connected';
      case 'warning':
        return 'warning';
      case 'disconnected':
        return 'disconnected';
      default:
        return 'disconnected';
    }
  };

  const handleSignOut = async () => {
    try {
      await authService?.signOut();
      navigate('/login');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  return (
    <>
      {isOpen && (
        <div
          className="sidebar-overlay"
          onClick={onClose}
          aria-hidden="true"
        />
      )}
      <aside
        className={`fixed left-0 top-0 h-full bg-white shadow-lg transform transition-transform duration-300 ease-in-out z-50 flex flex-col ${
          isOpen ? 'translate-x-0' : '-translate-x-full'
        } lg:translate-x-0 lg:static w-[280px]`}
      >
        <div className="sidebar-header">
          <div className="sidebar-header-content">
            {!isCollapsed ? (
              <>
                <div className="sidebar-logo-full">
                  <AppImage
                    src="/assets/images/RVA2Logo-Var_1_-1765525605212.png"
                    alt="Right Vectors logo featuring navy blue checkmark design with bronze accent"
                    className="h-10 w-auto object-contain mx-auto"
                  />
                  <span className="sidebar-logo-text text-center">HCC</span>
                </div>
              </>
            ) : (
              <div className="sidebar-favicon">
                <AppImage
                  src="/assets/images/RVA2Favicon-1765526524222.png"
                  alt="Right Vectors favicon"
                  className="h-8 w-8 object-contain"
                />
              </div>
            )}
          </div>
          <button
            onClick={toggleCollapse}
            className="sidebar-collapse-btn hidden lg:flex items-center justify-center w-8 h-8 rounded-md text-muted-foreground hover:bg-accent/10 hover:text-accent transition-all duration-150"
            aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            <Icon
              name={isCollapsed ? 'ChevronRight' : 'ChevronLeft'}
              size={20}
            />
          </button>
        </div>

        <div className="px-4 py-3 border-b border-border">
          <div className="status-indicator">
            <div className={`status-dot ${getStatusColor(systemStatus?.erp)}`} />
            <span className={isCollapsed ? 'hidden' : 'block'}>
              System Status
            </span>
          </div>
          {!isCollapsed && (
            <div className="mt-2 space-y-1 text-xs text-muted-foreground">
              <div className="flex items-center justify-between">
                <span>ERP</span>
                <div
                  className={`status-dot ${getStatusColor(systemStatus?.erp)}`}
                />
              </div>
              <div className="flex items-center justify-between">
                <span>Banking</span>
                <div
                  className={`status-dot ${getStatusColor(
                    systemStatus?.banking
                  )}`}
                />
              </div>
              <div className="flex items-center justify-between">
                <span>Shipping</span>
                <div
                  className={`status-dot ${getStatusColor(
                    systemStatus?.shipping
                  )}`}
                />
              </div>
            </div>
          )}
        </div>

        <nav className="sidebar-nav">
          {navigationItems?.map((group, groupIndex) => (
            <div key={groupIndex} className="sidebar-nav-group">
              <div className="sidebar-nav-group-label">{group?.group}</div>
              {group?.items?.map((item, itemIndex) => (
                <div
                  key={itemIndex}
                  className={`sidebar-nav-item ${
                    isActive(item?.path) ? 'active' : ''
                  } ${item?.action === 'logout' ? 'text-red-600 hover:bg-red-50' : ''}`}
                  onClick={() => handleNavigation(item?.path, item?.action)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e?.key === 'Enter' || e?.key === ' ') {
                      e?.preventDefault();
                      handleNavigation(item?.path, item?.action);
                    }
                  }}
                >
                  <Icon name={item?.icon} size={20} />
                  <span className="sidebar-nav-item-text">{item?.label}</span>
                </div>
              ))}
            </div>
          ))}
        </nav>

        <div className="sidebar-footer">
          <div className="role-badge mb-3">
            <Icon name="Shield" size={14} />
            {!isCollapsed && <span>Right Vectors</span>}
          </div>
          <div
            className="sidebar-toggle"
            onClick={() => {
              console.log('User settings clicked');
            }}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e?.key === 'Enter' || e?.key === ' ') {
                e?.preventDefault();
                console.log('User settings clicked');
              }
            }}
          >
            <Icon name="Settings" size={20} />
            {!isCollapsed && <span className="ml-3">Settings</span>}
          </div>
        </div>

        {/* User Profile Section */}
        {profile && (
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center">
              <div className="h-10 w-10 rounded-full bg-indigo-600 flex items-center justify-center text-white font-semibold">
                {profile?.fullName?.charAt(0) || profile?.email?.charAt(0) || 'U'}
              </div>
              <div className="ml-3 flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">
                  {profile?.fullName || 'User'}
                </p>
                <p className="text-xs text-gray-500 truncate">
                  {profile?.role?.replace(/_/g, ' ') || 'User'}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Sign Out Button */}
        <div className="px-6 py-4 border-t border-gray-200">
          <button
            onClick={handleSignOut}
            className="w-full flex items-center px-4 py-2 text-sm font-medium text-red-600 hover:bg-red-50 rounded-lg transition-colors"
          >
            <svg
              className="mr-3 h-5 w-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
              />
            </svg>
            Sign Out
          </button>
        </div>
      </aside>
    </>
  );
}